import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import { mutualisteAPI, parameterAPI } from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const AdminMutualisteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Récupérer les données du mutualiste
  const { data: mutualisteData, isLoading: mutualisteLoading, refetch: refetchMutualiste } = useQuery(
    ['mutualiste', id],
    () => mutualisteAPI.getMutualiste(id),
    {
      onError: (error) => {
        console.error('Erreur récupération mutualiste:', error);
        toast.error('Erreur lors du chargement des informations');
      }
    }
  );

  // Récupérer les options de paramétrage
  const { data: parametersData, isLoading: parametersLoading } = useQuery(
    'parameters-options',
    () => parameterAPI.getActiveOptions(),
    {
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
        console.error('Erreur récupération options:', error);
        toast.error('Erreur lors du chargement des options');
      }
    }
  );

  const [formData, setFormData] = useState({
    matricule: '',
    nom: '',
    prenoms: '',
    genre: '',
    dateNaissance: '',
    lieuNaissance: '',
    nationalite: '',
    situationMatrimoniale: '',
    
    // Informations professionnelles
    entreprise: '',
    fonction: '',
    emailProfessionnel: '',
    telephoneProfessionnel: '',
    adresseProfessionnelle: '',
    
    // Informations de résidence
    adresseResidence: '',
    ville: '',
    codePostal: '',
    pays: '',
    
    // Informations de contact
    mobile1: '',
    mobile2: '',
    emailPersonnel: '',
    adressePostale: '',
    
    // Type
    type: 'ADHERENT'
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFictifMatricule, setIsFictifMatricule] = useState(false);

  // Charger les données du mutualiste dans le formulaire
  useEffect(() => {
    if (mutualisteData?.data) {
      const mutualiste = mutualisteData.data;
      
      // Vérifier si le matricule est un matricule fictif (auto-créé, se terminant par 9999)
      const isFictif = mutualiste.matricule?.endsWith('9999') || false;
      setIsFictifMatricule(isFictif);
      
      setFormData({
        matricule: mutualiste.matricule || '',
        nom: mutualiste.nom || '',
        prenoms: mutualiste.prenoms || '',
        genre: (mutualiste.genre?.id || mutualiste.genreId || ''),
        dateNaissance: mutualiste.dateNaissance ? mutualiste.dateNaissance.split('T')[0] : '',
        lieuNaissance: mutualiste.lieuNaissance || '',
        nationalite: mutualiste.nationalite || '',
        situationMatrimoniale: (mutualiste.situationMatrimoniale?.id || mutualiste.situationMatrimonialeId || ''),
        
        entreprise: (mutualiste.enterprise?.id || mutualiste.entrepriseId || ''),
        fonction: (mutualiste.fonction?.id || mutualiste.fonctionId || ''),
        emailProfessionnel: mutualiste.emailProfessionnel || '',
        telephoneProfessionnel: mutualiste.telephoneProfessionnel || '',
        adresseProfessionnelle: mutualiste.adresseProfessionnelle || '',
        
        adresseResidence: mutualiste.adresseResidence || '',
        ville: mutualiste.ville || '',
        codePostal: mutualiste.codePostal || '',
        pays: mutualiste.pays || '',
        
        mobile1: mutualiste.mobile1 || '',
        mobile2: mutualiste.mobile2 || '',
        emailPersonnel: mutualiste.emailPersonnel || '',
        adressePostale: mutualiste.adressePostale || '',
        
        type: mutualiste.type || 'ADHERENT'
      });
    }
  }, [mutualisteData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur pour ce champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Champs obligatoires
    if (!formData.nom.trim()) newErrors.nom = 'Le nom est obligatoire';
    if (!formData.prenoms.trim()) newErrors.prenoms = 'Les prénoms sont obligatoires';
    if (!formData.genre) newErrors.genre = 'Le genre est obligatoire';
    if (!formData.dateNaissance) newErrors.dateNaissance = 'La date de naissance est obligatoire';
    if (!formData.adresseResidence.trim()) newErrors.adresseResidence = 'L\'adresse de résidence est obligatoire';
    if (!formData.ville.trim()) newErrors.ville = 'La ville est obligatoire';
    if (!formData.mobile1.trim()) newErrors.mobile1 = 'Le mobile principal est obligatoire';

    // Validation email
    if (formData.emailPersonnel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailPersonnel)) {
      newErrors.emailPersonnel = 'Format d\'email invalide';
    }
    if (formData.emailProfessionnel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailProfessionnel)) {
      newErrors.emailProfessionnel = 'Format d\'email invalide';
    }

    // Validation téléphone (format simple)
    if (formData.mobile1 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile1)) {
      newErrors.mobile1 = 'Format de téléphone invalide';
    }
    if (formData.mobile2 && !/^[0-9+\-\s()]{8,}$/.test(formData.mobile2)) {
      newErrors.mobile2 = 'Format de téléphone invalide';
    }
    if (formData.telephoneProfessionnel && !/^[0-9+\-\s()]{8,}$/.test(formData.telephoneProfessionnel)) {
      newErrors.telephoneProfessionnel = 'Format de téléphone invalide';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setIsLoading(true);
    try {
      // Créer une copie des données sans le champ type (non modifiable)
      const { type, ...dataToSend } = formData;

      // Envoyer l'intégralité des champs du formulaire (y compris facultatifs)
      console.log('📤 Données envoyées:', dataToSend);
      
      await mutualisteAPI.updateMutualiste(id, dataToSend);
      
      // Invalider le cache pour forcer le rechargement des données
      await refetchMutualiste();
      
      toast.success('Mutualiste modifié avec succès');
      navigate(`/admin/mutualistes/${id}`);
    } catch (error) {
      console.error('❌ Erreur modification mutualiste:', error);
      console.error('❌ Détails:', error.response?.data);
      toast.error(error.response?.data?.message || 'Erreur lors de la modification du mutualiste');
    } finally {
      setIsLoading(false);
    }
  };

  if (mutualisteLoading || parametersLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!mutualisteData?.data) {
    return (
      <div className="bg-white rounded-xl shadow-card p-12 text-center">
        <p className="text-red-500">Mutualiste non trouvé</p>
        <Link
          to="/admin/mutualistes"
          className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Retour à la liste
        </Link>
      </div>
    );
  }

  const genres = parametersData?.data?.data?.genres || [];
  const situationsMatrimoniales = parametersData?.data?.data?.situationMatrimoniale || [];
  const entreprises = parametersData?.data?.data?.entreprises || [];
  const fonctions = parametersData?.data?.data?.fonctions || [];

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/admin/mutualistes/${id}`}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Retour aux détails
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center mb-6">
          <div className="p-3 rounded-lg bg-blue-100 text-blue-600 mr-4">
            <PencilIcon className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary-900">
              Modifier le mutualiste
            </h1>
            <p className="text-gray-600">
              Mettre à jour les informations de {formData.prenoms} {formData.nom}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informations personnelles */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informations personnelles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Matricule
                </label>
                <input
                  type="text"
                  name="matricule"
                  value={formData.matricule}
                  onChange={isFictifMatricule ? handleInputChange : undefined}
                  disabled={!isFictifMatricule}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${
                    isFictifMatricule 
                      ? 'bg-white cursor-text focus:ring-2 focus:ring-blue-500 focus:border-blue-500' 
                      : 'bg-gray-100 cursor-not-allowed'
                  }`}
                />
                <p className={`mt-1 text-xs ${isFictifMatricule ? 'text-red-600' : 'text-gray-500'}`}>
                  {isFictifMatricule 
                    ? 'Matricule à remplacer (format: 6-7 chiffres + 1 lettre + 4 chiffres)' 
                    : 'Le matricule ne peut pas être modifié'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <input
                  type="text"
                  value={formData.type === 'ADHERENT' ? 'Adhérent' : 'Ayant-droit'}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">Le type ne peut pas être modifié</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.nom ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.nom && (
                  <p className="mt-1 text-sm text-red-500">{errors.nom}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénoms <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prenoms"
                  value={formData.prenoms}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.prenoms ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.prenoms && (
                  <p className="mt-1 text-sm text-red-500">{errors.prenoms}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Genre <span className="text-red-500">*</span>
                </label>
                <select
                  name="genre"
                  value={formData.genre}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.genre ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Sélectionner...</option>
                  {genres.map(genre => (
                    <option key={genre.id} value={genre.id}>
                      {genre.libelle}
                    </option>
                  ))}
                </select>
                {errors.genre && (
                  <p className="mt-1 text-sm text-red-500">{errors.genre}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date de naissance <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="dateNaissance"
                  value={formData.dateNaissance}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.dateNaissance ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.dateNaissance && (
                  <p className="mt-1 text-sm text-red-500">{errors.dateNaissance}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu de naissance
                </label>
                <input
                  type="text"
                  name="lieuNaissance"
                  value={formData.lieuNaissance}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nationalité
                </label>
                <input
                  type="text"
                  name="nationalite"
                  value={formData.nationalite}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Situation matrimoniale
                </label>
                <select
                  name="situationMatrimoniale"
                  value={formData.situationMatrimoniale}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Sélectionner...</option>
                  {situationsMatrimoniales.map(situation => (
                    <option key={situation.id} value={situation.id}>
                      {situation.libelle}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Informations professionnelles */}
          {formData.type === 'ADHERENT' && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Informations professionnelles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entreprise
                  </label>
                  <select
                    name="entreprise"
                    value={formData.entreprise}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner...</option>
                    {entreprises.map(entreprise => (
                      <option key={entreprise.id} value={entreprise.id}>
                        {entreprise.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fonction
                  </label>
                  <select
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">Sélectionner...</option>
                    {fonctions.map(fonction => (
                      <option key={fonction.id} value={fonction.id}>
                        {fonction.libelle}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email professionnel
                  </label>
                  <input
                    type="email"
                    name="emailProfessionnel"
                    value={formData.emailProfessionnel}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                      errors.emailProfessionnel ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.emailProfessionnel && (
                    <p className="mt-1 text-sm text-red-500">{errors.emailProfessionnel}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Téléphone professionnel
                  </label>
                  <input
                    type="tel"
                    name="telephoneProfessionnel"
                    value={formData.telephoneProfessionnel}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                      errors.telephoneProfessionnel ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.telephoneProfessionnel && (
                    <p className="mt-1 text-sm text-red-500">{errors.telephoneProfessionnel}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adresse professionnelle
                  </label>
                  <input
                    type="text"
                    name="adresseProfessionnelle"
                    value={formData.adresseProfessionnelle}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Informations de résidence */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informations de résidence
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse de résidence <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="adresseResidence"
                  value={formData.adresseResidence}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.adresseResidence ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.adresseResidence && (
                  <p className="mt-1 text-sm text-red-500">{errors.adresseResidence}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ville <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="ville"
                  value={formData.ville}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.ville ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.ville && (
                  <p className="mt-1 text-sm text-red-500">{errors.ville}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Code postal
                </label>
                <input
                  type="text"
                  name="codePostal"
                  value={formData.codePostal}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pays
                </label>
                <input
                  type="text"
                  name="pays"
                  value={formData.pays}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse postale
                </label>
                <input
                  type="text"
                  name="adressePostale"
                  value={formData.adressePostale}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Informations de contact */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Informations de contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile 1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile1"
                  value={formData.mobile1}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile1 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile1 && (
                  <p className="mt-1 text-sm text-red-500">{errors.mobile1}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile 2
                </label>
                <input
                  type="tel"
                  name="mobile2"
                  value={formData.mobile2}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mobile2 ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mobile2 && (
                  <p className="mt-1 text-sm text-red-500">{errors.mobile2}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email personnel
                </label>
                <input
                  type="email"
                  name="emailPersonnel"
                  value={formData.emailPersonnel}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.emailPersonnel ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.emailPersonnel && (
                  <p className="mt-1 text-sm text-red-500">{errors.emailPersonnel}</p>
                )}
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <Link
              to={`/admin/mutualistes/${id}`}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </Link>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary flex items-center"
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="small" className="mr-2" />
                  Modification en cours...
                </>
              ) : (
                <>
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminMutualisteEdit;
